// ============ INTERACTIVE SCALE COMPONENT FOR CONSULTATION WORKFLOW ============
// Compact version of scales for embedding in consultation flow

import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Progress } from "../components/ui/progress";
import { ChevronDown, ChevronUp, CheckCircle2, Info } from "lucide-react";

import {
  BARTHEL_ITEMS, interpretBarthel,
  MMSE_SECTIONS, interpretMMSE,
  MNA_ITEMS, interpretMNA,
  YESAVAGE_ITEMS, interpretYesavage, scoreYesavageFromAnswers,
  PHQ9_ITEMS, interpretPhq9,
  GAD7_ITEMS, interpretGad7,
  KATZ_ITEMS, interpretKatz,
  LAWTON_ITEMS, interpretLawton,
  MORSE_ITEMS, interpretMorse,
  TINETTI_BALANCE_ITEMS, TINETTI_GAIT_ITEMS, interpretTinetti,
  SARC_F_ITEMS, interpretSarcF,
  CFS_ITEMS, interpretCFS,
  FRAIL_ITEMS, interpretFRAIL,
  PFEIFFER_ITEMS, interpretPfeiffer,
  NORTON_ITEMS, interpretNorton,
  GIJON_ITEMS, interpretGijon,
  ZARIT_ITEMS, interpretZarit,
  interpretTug,
  interpretMoca,
  CAM_ITEMS, evaluateCam, interpretCam,
  evaluateMiniCog, interpretMiniCog,
  CHARLSON_ITEMS, interpretCharlson,
  interpretEvaDolor,
  scoreSppbGait, scoreSppbChair, interpretSppb,
  POLIFARMACIA_SIMPLE_FLAG_ITEMS,
  interpretPolifarmaciaSimple,
} from "../lib/scales-data";
import { calculateScoreFromAnswers } from "../lib/scaleScoring";

// ============ SCALE RENDERER COMPONENT ============
export const InteractiveScaleCard = ({ 
  scaleId, 
  scaleName, 
  answers, 
  onAnswersChange,
  initiallyExpanded = false 
}) => {
  const [expanded, setExpanded] = useState(initiallyExpanded);
  const scrollBodyRef = useRef(null);
  const [showScrollMoreHint, setShowScrollMoreHint] = useState(false);

  const updateScrollMoreHint = useCallback(() => {
    const el = scrollBodyRef.current;
    if (!el) {
      setShowScrollMoreHint(false);
      return;
    }
    const hasOverflow = el.scrollHeight > el.clientHeight + 2;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 6;
    setShowScrollMoreHint(hasOverflow && !nearBottom);
  }, []);

  useEffect(() => {
    if (!expanded) {
      setShowScrollMoreHint(false);
      return;
    }
    const id = window.requestAnimationFrame(() => updateScrollMoreHint());
    return () => window.cancelAnimationFrame(id);
  }, [expanded, answers, scaleId, updateScrollMoreHint]);

  useEffect(() => {
    window.addEventListener("resize", updateScrollMoreHint);
    return () => window.removeEventListener("resize", updateScrollMoreHint);
  }, [updateScrollMoreHint]);

  // Get scale configuration
  const scaleConfig = useMemo(() => {
    switch (scaleId) {
      case "barthel":
        return {
          items: BARTHEL_ITEMS,
          maxScore: 100,
          interpret: interpretBarthel,
          type: "radio"
        };
      case "mmse":
        return {
          sections: MMSE_SECTIONS,
          maxScore: 30,
          interpret: interpretMMSE,
          type: "binary"
        };
      case "mna_sf":
        return {
          items: MNA_ITEMS,
          maxScore: 14,
          interpret: interpretMNA,
          type: "radio"
        };
      case "yesavage":
        return {
          items: YESAVAGE_ITEMS,
          maxScore: 15,
          interpret: interpretYesavage,
          type: "yesno"
        };
      case "phq9":
        return {
          items: PHQ9_ITEMS,
          maxScore: 27,
          interpret: interpretPhq9,
          type: "radio"
        };
      case "gad7":
        return {
          items: GAD7_ITEMS,
          maxScore: 21,
          interpret: interpretGad7,
          type: "radio"
        };
      case "katz":
        return {
          items: KATZ_ITEMS,
          maxScore: 6,
          interpret: interpretKatz,
          type: "radio"
        };
      case "lawton":
        return {
          items: LAWTON_ITEMS,
          maxScore: 8,
          interpret: interpretLawton,
          type: "radio"
        };
      case "morse":
        return {
          items: MORSE_ITEMS,
          maxScore: 125,
          interpret: interpretMorse,
          type: "radio"
        };
      case "tinetti":
        return {
          balanceItems: TINETTI_BALANCE_ITEMS,
          gaitItems: TINETTI_GAIT_ITEMS,
          maxScore: 28,
          interpret: interpretTinetti,
          type: "tinetti"
        };
      case "frail":
        return {
          items: FRAIL_ITEMS,
          maxScore: 5,
          interpret: interpretFRAIL,
          type: "radio"
        };
      case "sarc_f":
        return {
          items: SARC_F_ITEMS,
          maxScore: 10,
          interpret: interpretSarcF,
          type: "radio"
        };
      case "cfs":
        return {
          items: CFS_ITEMS,
          maxScore: 9,
          interpret: interpretCFS,
          type: "radio"
        };
      case "pfeiffer":
        return {
          items: PFEIFFER_ITEMS,
          maxScore: 10,
          interpret: interpretPfeiffer,
          type: "errors"
        };
      case "norton":
        return {
          items: NORTON_ITEMS,
          maxScore: 20,
          interpret: interpretNorton,
          type: "radio"
        };
      case "gijon":
        return {
          items: GIJON_ITEMS,
          maxScore: 25,
          interpret: interpretGijon,
          type: "radio"
        };
      case "zarit":
        return {
          items: ZARIT_ITEMS,
          maxScore: 48,
          interpret: interpretZarit,
          type: "radio"
        };
      case "tug":
        return {
          type: "seconds",
          maxScore: 60,
          interpret: interpretTug,
        };
      case "moca":
        return {
          type: "score_0_30",
          maxScore: 30,
          interpret: interpretMoca,
        };
      case "mini_cog":
        return {
          type: "mini_cog",
          maxScore: 1,
          interpret: interpretMiniCog,
        };
      case "cam":
        return {
          items: CAM_ITEMS,
          type: "cam",
          maxScore: 1,
          interpret: interpretCam,
        };
      case "charlson":
        return {
          type: "charlson_checklist",
          maxScore: 37,
          interpret: interpretCharlson,
        };
      case "eva_dolor":
        return {
          type: "score_0_10",
          maxScore: 10,
          interpret: interpretEvaDolor,
        };
      case "sppb":
        return {
          type: "sppb",
          maxScore: 12,
          interpret: interpretSppb,
        };
      case "polifarmacia_simple":
        return {
          type: "polifarmacia_simple",
          maxScore: 30,
        };
      default:
        return null;
    }
  }, [scaleId]);

  // Calculate score based on answers
  const { score, totalItems, answeredItems, interpretation } = useMemo(() => {
    if (!scaleConfig) return { score: 0, totalItems: 0, answeredItems: 0, interpretation: null };

    let total = 0;
    let itemCount = 0;
    let answered = 0;

    if (scaleConfig.type === "seconds") {
      itemCount = 1;
      const raw = answers.seconds;
      if (raw !== undefined && raw !== "" && String(raw).trim() !== "") {
        const n = parseFloat(String(raw).replace(",", "."));
        if (!Number.isNaN(n) && n >= 0) {
          answered = 1;
          total = n;
        }
      }
    } else if (scaleConfig.type === "score_0_30") {
      itemCount = 1;
      const raw = answers.score;
      if (raw !== undefined && raw !== "" && String(raw).trim() !== "") {
        const n = parseInt(raw, 10);
        if (!Number.isNaN(n) && n >= 0 && n <= 30) {
          answered = 1;
          total = n;
        }
      }
    } else if (scaleConfig.type === "charlson_checklist") {
      itemCount = CHARLSON_ITEMS.length;
      total = CHARLSON_ITEMS.reduce((sum, item) => (answers?.[item.id] ? sum + item.weight : sum), 0);
      answered = CHARLSON_ITEMS.some((item) => !!answers?.[item.id]) ? itemCount : 0;
    } else if (scaleConfig.type === "score_0_10") {
      itemCount = 1;
      const raw = answers.score;
      if (raw !== undefined && raw !== "" && String(raw).trim() !== "") {
        const n = parseInt(raw, 10);
        if (!Number.isNaN(n) && n >= 0 && n <= 10) {
          answered = 1;
          total = n;
        }
      }
    } else if (scaleConfig.type === "polifarmacia_simple") {
      itemCount = 1;
      const raw = answers?.num_medicamentos;
      if (raw !== undefined && raw !== "" && String(raw).trim() !== "") {
        const n = parseInt(String(raw).trim(), 10);
        if (!Number.isNaN(n) && n >= 0 && n <= 99) {
          answered = 1;
          total = n;
        }
      }
    } else if (scaleConfig.type === "cam") {
      itemCount = CAM_ITEMS.length;
      CAM_ITEMS.forEach((item) => {
        const raw = answers[item.id];
        if (raw === "si" || raw === "no") answered++;
      });
      total = answered === itemCount ? evaluateCam(answers) : null;
    } else if (scaleConfig.type === "mini_cog") {
      itemCount = 2;
      const hasRecall = answers?.recuerdo_3_palabras !== undefined && answers?.recuerdo_3_palabras !== "";
      const hasClock = answers?.reloj_normal === true || answers?.reloj_normal === false || answers?.reloj_normal === "true" || answers?.reloj_normal === "false";
      answered = (hasRecall ? 1 : 0) + (hasClock ? 1 : 0);
      total = answered === itemCount ? evaluateMiniCog(answers) : null;
    } else if (scaleConfig.type === "sppb") {
      itemCount = 3;
      const balance = Number(answers?.balance);
      const balanceValid = !Number.isNaN(balance) && balance >= 0 && balance <= 4;
      const gaitScore = scoreSppbGait(answers?.marcha_segundos, !!answers?.marcha_no_puede);
      const chairScore = scoreSppbChair(answers?.silla_segundos, !!answers?.silla_no_puede);
      const gaitValid = gaitScore !== null;
      const chairValid = chairScore !== null;
      answered = (balanceValid ? 1 : 0) + (gaitValid ? 1 : 0) + (chairValid ? 1 : 0);
      total = answered === itemCount ? balance + gaitScore + chairScore : 0;
    } else if (scaleConfig.type === "binary" && scaleConfig.sections) {
      // MMSE
      scaleConfig.sections.forEach(section => {
        section.items.forEach(item => {
          itemCount++;
          if (answers[item.id] !== undefined) {
            answered++;
            total += answers[item.id];
          }
        });
      });
    } else if (scaleConfig.type === "yesno") {
      // Yesavage GDS-15: puntaje único vía scoreYesavageFromAnswers (q1–q15; compatible y1–y15)
      itemCount = scaleConfig.items.length;
      scaleConfig.items.forEach((item) => {
        const legacyKey = `y${String(item.id).replace(/^q/, "")}`;
        const ans = answers[item.id] ?? answers[legacyKey];
        if (ans !== undefined) answered++;
      });
      total = scaleId === "yesavage" ? scoreYesavageFromAnswers(answers) : 0;
    } else if (scaleConfig.type === "errors") {
      // Pfeiffer - count errors
      scaleConfig.items.forEach(item => {
        itemCount++;
        if (answers[item.id] !== undefined) {
          answered++;
          if (answers[item.id] === 0) total++; // 0 = error
        }
      });
    } else if (scaleConfig.type === "tinetti") {
      // Tinetti has two sections
      [...scaleConfig.balanceItems, ...scaleConfig.gaitItems].forEach(item => {
        itemCount++;
        if (answers[item.id] !== undefined) {
          answered++;
          total += answers[item.id];
        }
      });
    } else {
      // Standard radio items (includes Lawton with optId for unique selection)
      scaleConfig.items.forEach(item => {
        itemCount++;
        const itemId = item.id || item.name;
        const raw = answers[itemId];
        if (raw !== undefined) {
          answered++;
          const hasOptId = item.options?.[0]?.optId !== undefined;
          if (hasOptId) {
            const opt = item.options.find(o => o.optId === raw || o.optId === parseInt(raw));
            total += opt ? opt.value : 0;
          } else {
            total += parseInt(raw) || 0;
          }
        }
      });
    }

    let interp = null;
    if (scaleConfig.type === "polifarmacia_simple") {
      if (answered === itemCount && itemCount > 0) {
        interp = interpretPolifarmaciaSimple(total, {
          duplicados: !!answers?.duplicados,
          potencialmente_inapropiados: !!answers?.potencialmente_inapropiados,
          dificultad_adherencia: !!answers?.dificultad_adherencia,
        });
      }
    } else if (scaleConfig.interpret) {
      interp = scaleConfig.interpret(total);
    }

    const persisted = calculateScoreFromAnswers(scaleId, answers);
    const score = persisted !== null && persisted !== undefined ? persisted : total;

    return {
      score,
      totalItems: itemCount,
      answeredItems: answered,
      interpretation: interp
    };
  }, [answers, scaleConfig, scaleId]);

  const progress = totalItems > 0 ? (answeredItems / totalItems) * 100 : 0;
  const isComplete = answeredItems === totalItems && totalItems > 0;

  if (!scaleConfig) {
    return (
      <Card className="border-slate-200">
        <CardContent className="p-4 text-center text-slate-500">
          Escala no disponible para evaluación interactiva
        </CardContent>
      </Card>
    );
  }

  const renderItems = () => {
    if (scaleConfig.type === "seconds") {
      const v = answers.seconds;
      return (
        <div className="p-3 bg-slate-50 rounded-lg space-y-2">
          <Label className="text-sm font-medium">Tiempo total (segundos)</Label>
          <Input
            type="number"
            inputMode="decimal"
            min={0}
            step={0.1}
            value={v === undefined || v === "" ? "" : v}
            onChange={(e) => {
              const t = e.target.value;
              if (t === "" || t === ".") onAnswersChange({ ...answers, seconds: t });
              else onAnswersChange({ ...answers, seconds: t });
            }}
            className="max-w-[200px]"
            placeholder="Ej. 9.5"
          />
          <p className="text-xs text-slate-500 leading-snug">
            Levantarse, caminar ~3 m, girar, volver y sentarse. Registrar el tiempo en segundos (se permiten decimales).
          </p>
        </div>
      );
    }
    if (scaleConfig.type === "score_0_30") {
      const v = answers.score;
      return (
        <div className="p-3 bg-slate-50 rounded-lg space-y-2">
          <Label className="text-sm font-medium">Puntaje total MoCA (0-30)</Label>
          <Input
            type="number"
            inputMode="numeric"
            min={0}
            max={30}
            step={1}
            value={v === undefined || v === "" ? "" : v}
            onChange={(e) => {
              const t = e.target.value;
              if (t === "") {
                onAnswersChange({ ...answers, score: "" });
                return;
              }
              const n = parseInt(t, 10);
              if (Number.isNaN(n)) return;
              onAnswersChange({ ...answers, score: Math.max(0, Math.min(30, n)) });
            }}
            className="max-w-[200px]"
            placeholder="Ej. 24"
          />
          <p className="text-xs text-slate-500 leading-snug">
            Registrar el puntaje total de la escala MoCA. Rango válido: 0 a 30.
          </p>
        </div>
      );
    }
    if (scaleConfig.type === "charlson_checklist") {
      const groups = [1, 2, 3, 6];
      return (
        <div className="p-3 bg-slate-50 rounded-lg space-y-2">
          <Label className="text-sm font-medium">Comorbilidades (cálculo automático)</Label>
          {groups.map((weight) => (
            <div key={weight} className="space-y-1">
              <p className="text-xs font-semibold text-slate-600">Peso {weight}</p>
              {CHARLSON_ITEMS.filter((item) => item.weight === weight).map((item) => (
                <Button
                  key={item.id}
                  type="button"
                  size="sm"
                  variant={answers?.[item.id] ? "default" : "outline"}
                  className="w-full justify-start h-auto py-1.5 text-xs"
                  onClick={() => onAnswersChange({ ...answers, [item.id]: !answers?.[item.id] })}
                >
                  {item.name}
                </Button>
              ))}
            </div>
          ))}
          <p className="text-xs text-slate-600 pt-1">
            Puntaje total calculado: <span className="font-semibold">{score}</span>
          </p>
        </div>
      );
    }
    if (scaleConfig.type === "score_0_10") {
      const v = answers.score;
      return (
        <div className="p-3 bg-slate-50 rounded-lg space-y-2">
          <Label className="text-sm font-medium">Puntaje EVA dolor (0-10)</Label>
          <Input
            type="number"
            inputMode="numeric"
            min={0}
            max={10}
            step={1}
            value={v === undefined || v === "" ? "" : v}
            onChange={(e) => {
              const t = e.target.value;
              if (t === "") {
                onAnswersChange({ ...answers, score: "" });
                return;
              }
              const n = parseInt(t, 10);
              if (Number.isNaN(n)) return;
              onAnswersChange({ ...answers, score: Math.max(0, Math.min(10, n)) });
            }}
            className="max-w-[200px]"
            placeholder="Ej. 6"
          />
        </div>
      );
    }
    if (scaleConfig.type === "polifarmacia_simple") {
      const v = answers?.num_medicamentos;
      return (
        <div className="space-y-4 p-3 bg-slate-50 rounded-lg">
          <div className="space-y-2">
            <Label className="text-sm font-medium">¿Cuántos medicamentos toma actualmente el paciente?</Label>
            <Input
              type="number"
              inputMode="numeric"
              min={0}
              max={99}
              step={1}
              value={v === undefined || v === "" ? "" : v}
              onChange={(e) => {
                const t = e.target.value;
                if (t === "") {
                  onAnswersChange({ ...answers, num_medicamentos: "" });
                  return;
                }
                const n = parseInt(t, 10);
                if (Number.isNaN(n)) return;
                onAnswersChange({ ...answers, num_medicamentos: Math.max(0, Math.min(99, n)) });
              }}
              className="max-w-[200px]"
              placeholder="Ej. 7"
            />
            <p className="text-xs text-slate-500 leading-snug">
              Recuento de medicamentos de uso regular (prescripción y/o automedicación habitual). No es un puntaje sumado: el riesgo se clasifica por el número total.
            </p>
          </div>
          <div className="space-y-2 border-t border-slate-200 pt-3">
            <p className="text-xs font-semibold text-slate-700">Señales asociadas (opcional)</p>
            <div className="space-y-2.5">
              {POLIFARMACIA_SIMPLE_FLAG_ITEMS.map((item) => (
                <div key={item.id} className="flex items-start gap-2">
                  <Checkbox
                    id={`polifarmacia-${item.id}`}
                    checked={!!answers?.[item.id]}
                    onCheckedChange={(checked) =>
                      onAnswersChange({ ...answers, [item.id]: checked === true })
                    }
                    className="mt-0.5"
                  />
                  <Label htmlFor={`polifarmacia-${item.id}`} className="text-xs font-normal text-slate-700 leading-snug cursor-pointer">
                    {item.label}
                  </Label>
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    }
    if (scaleConfig.type === "cam") {
      return (
        <div className="space-y-3">
          <div className="flex gap-2 rounded-lg border border-sky-100 bg-sky-50/80 p-3 text-xs text-slate-700 leading-snug">
            <Info className="h-4 w-4 shrink-0 text-sky-600 mt-0.5" aria-hidden />
            <p>
              <span className="font-semibold text-slate-800">Regla CAM:</span> resultado positivo si están presentes los criterios{" "}
              <strong>1 y 2</strong>, y además <strong>3 o 4</strong>. No se suman puntajes: es una decisión algorítmica sí/no.
            </p>
          </div>
          {CAM_ITEMS.map((item, idx) => (
            <div key={item.id} className="flex flex-col gap-1 p-2 bg-slate-50 rounded-lg border border-slate-100">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-start gap-2 flex-1 min-w-0">
                  <span className="text-xs font-bold text-slate-500 shrink-0">{idx + 1}</span>
                  <div>
                    <span className="text-xs font-medium text-slate-800">{item.name.replace(/^\d+\.\s*/, "")}</span>
                    {item.description && <p className="text-[11px] text-slate-500 mt-1 leading-snug">{item.description}</p>}
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-end gap-1 sm:justify-start pl-6">
                <div className="flex gap-1">
                  <Button
                    type="button"
                    size="sm"
                    variant={answers[item.id] === "si" ? "default" : "outline"}
                    className={`h-6 px-2 text-xs ${answers[item.id] === "si" ? "bg-red-500" : ""}`}
                    onClick={() => onAnswersChange({ ...answers, [item.id]: "si" })}
                  >
                    Sí
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={answers[item.id] === "no" ? "default" : "outline"}
                    className={`h-6 px-2 text-xs ${answers[item.id] === "no" ? "bg-emerald-500" : ""}`}
                    onClick={() => onAnswersChange({ ...answers, [item.id]: "no" })}
                  >
                    No
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      );
    }
    if (scaleConfig.type === "mini_cog") {
      const recall = answers?.recuerdo_3_palabras;
      const clock = answers?.reloj_normal;
      return (
        <div className="p-3 bg-slate-50 rounded-lg space-y-3">
          <div className="space-y-1">
            <Label className="text-sm font-medium">Recuerdo de 3 palabras (0-3)</Label>
            <Input
              type="number"
              inputMode="numeric"
              min={0}
              max={3}
              step={1}
              value={recall === undefined || recall === "" ? "" : recall}
              onChange={(e) => {
                const t = e.target.value;
                if (t === "") return onAnswersChange({ ...answers, recuerdo_3_palabras: "" });
                const n = parseInt(t, 10);
                if (Number.isNaN(n)) return;
                onAnswersChange({ ...answers, recuerdo_3_palabras: Math.max(0, Math.min(3, n)) });
              }}
              className="max-w-[200px]"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-sm font-medium">Test del reloj</Label>
            <div className="flex gap-2">
              <Button
                type="button"
                size="sm"
                variant={clock === true || clock === "true" ? "default" : "outline"}
                className={`h-7 px-3 text-xs ${clock === true || clock === "true" ? "bg-emerald-600" : ""}`}
                onClick={() => onAnswersChange({ ...answers, reloj_normal: true })}
              >
                Normal
              </Button>
              <Button
                type="button"
                size="sm"
                variant={clock === false || clock === "false" ? "default" : "outline"}
                className={`h-7 px-3 text-xs ${clock === false || clock === "false" ? "bg-red-500" : ""}`}
                onClick={() => onAnswersChange({ ...answers, reloj_normal: false })}
              >
                Anormal
              </Button>
            </div>
          </div>
        </div>
      );
    }
    if (scaleConfig.type === "sppb") {
      const gaitScore = scoreSppbGait(answers?.marcha_segundos, !!answers?.marcha_no_puede);
      const chairScore = scoreSppbChair(answers?.silla_segundos, !!answers?.silla_no_puede);
      return (
        <div className="p-3 bg-slate-50 rounded-lg space-y-3">
          <div className="space-y-1">
            <Label className="text-sm font-medium">Balance (0-4)</Label>
            <Input
              type="number"
              min={0}
              max={4}
              step={1}
              value={answers?.balance === undefined || answers?.balance === "" ? "" : answers.balance}
              onChange={(e) => {
                const t = e.target.value;
                if (t === "") return onAnswersChange({ ...answers, balance: "" });
                const n = parseInt(t, 10);
                if (Number.isNaN(n)) return;
                onAnswersChange({ ...answers, balance: Math.max(0, Math.min(4, n)) });
              }}
              className="max-w-[140px]"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-sm font-medium">Velocidad de marcha (4 metros, segundos)</Label>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min={0}
                step={0.01}
                value={answers?.marcha_segundos === undefined || answers?.marcha_segundos === "" ? "" : answers.marcha_segundos}
                onChange={(e) => onAnswersChange({ ...answers, marcha_segundos: e.target.value, marcha_no_puede: false })}
                className="max-w-[160px]"
              />
              <Button
                type="button"
                size="sm"
                variant={answers?.marcha_no_puede ? "default" : "outline"}
                onClick={() => onAnswersChange({ ...answers, marcha_no_puede: !answers?.marcha_no_puede })}
              >
                No puede realizar
              </Button>
              <Badge variant="outline">Score: {gaitScore ?? "—"}</Badge>
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-sm font-medium">Silla (5 levantadas, segundos)</Label>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min={0}
                step={0.01}
                value={answers?.silla_segundos === undefined || answers?.silla_segundos === "" ? "" : answers.silla_segundos}
                onChange={(e) => onAnswersChange({ ...answers, silla_segundos: e.target.value, silla_no_puede: false })}
                className="max-w-[160px]"
              />
              <Button
                type="button"
                size="sm"
                variant={answers?.silla_no_puede ? "default" : "outline"}
                onClick={() => onAnswersChange({ ...answers, silla_no_puede: !answers?.silla_no_puede })}
              >
                No puede realizar
              </Button>
              <Badge variant="outline">Score: {chairScore ?? "—"}</Badge>
            </div>
          </div>
        </div>
      );
    }
    if (scaleConfig.type === "binary" && scaleConfig.sections) {
      // MMSE rendering
      return (
        <div className="space-y-4">
          {scaleConfig.sections.map(section => (
            <div key={section.id} className="p-3 bg-slate-50 rounded-lg">
              <div className="flex justify-between items-center mb-2">
                <h4 className="font-medium text-sm">{section.name}</h4>
                <Badge variant="outline" className="text-xs">
                  {section.items.reduce((sum, item) => sum + (answers[item.id] || 0), 0)}/{section.maxPoints}
                </Badge>
              </div>
              {section.instruction && (
                <p className="text-xs text-slate-500 mb-2 flex items-start gap-1">
                  <Info className="h-3 w-3 mt-0.5 flex-shrink-0" />
                  {section.instruction}
                </p>
              )}
              <div className="space-y-1">
                {section.items.map(item => (
                  <div key={item.id} className="flex items-center justify-between py-1 border-b border-slate-100 last:border-0">
                    <span className="text-xs text-slate-700 flex-1">{item.question}</span>
                    <div className="flex gap-1 ml-2">
                      <Button
                        type="button"
                        size="sm"
                        variant={answers[item.id] === 0 ? "default" : "outline"}
                        className={`h-6 w-6 p-0 text-xs ${answers[item.id] === 0 ? "bg-red-500" : ""}`}
                        onClick={() => onAnswersChange({ ...answers, [item.id]: 0 })}
                      >
                        0
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant={answers[item.id] === 1 ? "default" : "outline"}
                        className={`h-6 w-6 p-0 text-xs ${answers[item.id] === 1 ? "bg-emerald-500" : ""}`}
                        onClick={() => onAnswersChange({ ...answers, [item.id]: 1 })}
                      >
                        1
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      );
    }

    if (scaleConfig.type === "yesno") {
      // Yesavage
      return (
        <div className="space-y-2">
          {scaleConfig.items.map((item, idx) => {
            const legacyKey = `y${String(item.id).replace(/^q/, "")}`;
            const row = answers[item.id] ?? answers[legacyKey];
            return (
            <div key={item.id} className="flex items-center justify-between p-2 bg-slate-50 rounded">
              <div className="flex items-start gap-2 flex-1">
                <span className="text-xs font-bold text-slate-500 w-4">{idx + 1}</span>
                <span className="text-xs text-slate-700">{item.question}</span>
              </div>
              <div className="flex gap-1 ml-2">
                <Button
                  type="button"
                  size="sm"
                  variant={row === "si" ? "default" : "outline"}
                  className={`h-6 px-2 text-xs ${
                    row === "si"
                      ? (item.depression === "si" ? "bg-red-500" : "bg-emerald-500")
                      : ""
                  }`}
                  onClick={() => onAnswersChange({ ...answers, [item.id]: "si" })}
                >
                  Sí
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={row === "no" ? "default" : "outline"}
                  className={`h-6 px-2 text-xs ${
                    row === "no"
                      ? (item.depression === "no" ? "bg-red-500" : "bg-emerald-500")
                      : ""
                  }`}
                  onClick={() => onAnswersChange({ ...answers, [item.id]: "no" })}
                >
                  No
                </Button>
              </div>
            </div>
            );
          })}
        </div>
      );
    }

    if (scaleConfig.type === "errors") {
      // Pfeiffer
      return (
        <div className="space-y-2">
          {scaleConfig.items.map((item, idx) => (
            <div key={item.id} className="flex items-center justify-between p-2 bg-slate-50 rounded">
              <div className="flex items-start gap-2 flex-1">
                <span className="text-xs font-bold text-slate-500 w-4">{idx + 1}</span>
                <span className="text-xs text-slate-700">{item.question}</span>
              </div>
              <div className="flex gap-1 ml-2">
                <Button
                  type="button"
                  size="sm"
                  variant={answers[item.id] === 1 ? "default" : "outline"}
                  className={`h-6 px-2 text-xs ${answers[item.id] === 1 ? "bg-emerald-500" : ""}`}
                  onClick={() => onAnswersChange({ ...answers, [item.id]: 1 })}
                >
                  Correcto
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={answers[item.id] === 0 ? "default" : "outline"}
                  className={`h-6 px-2 text-xs ${answers[item.id] === 0 ? "bg-red-500" : ""}`}
                  onClick={() => onAnswersChange({ ...answers, [item.id]: 0 })}
                >
                  Error
                </Button>
              </div>
            </div>
          ))}
        </div>
      );
    }

    if (scaleConfig.type === "tinetti") {
      // Tinetti has balance + gait
      return (
        <div className="space-y-4">
          <div className="p-3 bg-slate-50 rounded-lg">
            <h4 className="font-medium text-sm mb-2">Equilibrio (16 pts)</h4>
            <div className="space-y-2">
              {scaleConfig.balanceItems.map(item => (
                <div key={item.id} className="space-y-1">
                  <Label className="text-xs">{item.name}</Label>
                  <div className="flex flex-wrap gap-1">
                    {item.options.map((opt, i) => (
                      <Button
                        key={i}
                        type="button"
                        size="sm"
                        variant={answers[item.id] === opt.value ? "default" : "outline"}
                        className="h-6 text-xs px-2"
                        onClick={() => onAnswersChange({ ...answers, [item.id]: opt.value })}
                      >
                        {opt.value}
                      </Button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="p-3 bg-slate-50 rounded-lg">
            <h4 className="font-medium text-sm mb-2">Marcha (12 pts)</h4>
            <div className="space-y-2">
              {scaleConfig.gaitItems.map(item => (
                <div key={item.id} className="space-y-1">
                  <Label className="text-xs">{item.name}</Label>
                  <div className="flex flex-wrap gap-1">
                    {item.options.map((opt, i) => (
                      <Button
                        key={i}
                        type="button"
                        size="sm"
                        variant={answers[item.id] === opt.value ? "default" : "outline"}
                        className="h-6 text-xs px-2"
                        onClick={() => onAnswersChange({ ...answers, [item.id]: opt.value })}
                      >
                        {opt.value}
                      </Button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    }

    // Default radio rendering for most scales
    const hasOptId = scaleConfig.items?.[0]?.options?.[0]?.optId !== undefined;
    return (
      <div className="space-y-3">
        {scaleConfig.items.map((item, idx) => {
          const itemId = item.id || item.name;
          const raw = answers[itemId];
          const pts = raw !== undefined && (hasOptId
            ? (item.options?.find(o => o.optId === raw || o.optId === parseInt(raw))?.value ?? 0)
            : parseInt(raw) || 0);
          const radioVal = hasOptId
            ? (raw !== undefined ? String(raw) : "")
            : (raw !== undefined ? String(raw) : "");
          return (
            <div key={itemId} className="p-3 bg-slate-50 rounded-lg">
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <Label className="text-sm font-medium">
                    {idx + 1}. {item.name || item.question}
                  </Label>
                  {item.description && (
                    <p className="text-xs text-slate-500 mt-0.5">{item.description}</p>
                  )}
                </div>
                {raw !== undefined && (
                  <Badge variant="outline" className="text-xs">
                    {pts} pts
                  </Badge>
                )}
              </div>
              <RadioGroup
                value={radioVal}
                onValueChange={(v) => {
                  const num = parseInt(v);
                  if (hasOptId) {
                    onAnswersChange({ ...answers, [itemId]: num });
                  } else {
                    onAnswersChange({ ...answers, [itemId]: num });
                  }
                }}
                className="space-y-1"
              >
                {item.options.map((opt, i) => {
                  const v = hasOptId ? String(opt.optId) : String(opt.value);
                  return (
                    <div key={i} className="flex items-center space-x-2 p-1.5 rounded hover:bg-slate-100">
                      <RadioGroupItem value={v} id={`${itemId}-${opt.optId ?? opt.value}-${i}`} />
                      <Label htmlFor={`${itemId}-${opt.optId ?? opt.value}-${i}`} className="text-xs cursor-pointer flex-1">
                        {opt.value !== undefined && <span className="font-medium">({opt.value})</span>} {opt.label}
                      </Label>
                    </div>
                  );
                })}
              </RadioGroup>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <Card className={`border-slate-200 overflow-hidden transition-all ${isComplete ? 'border-emerald-300' : ''}`}>
      <CardHeader 
        className="py-3 px-4 cursor-pointer hover:bg-slate-50"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {isComplete && <CheckCircle2 className="h-4 w-4 text-emerald-500" />}
            <CardTitle className="text-sm font-medium">{scaleName}</CardTitle>
          </div>
          <div className="flex items-center gap-2">
            {answeredItems > 0 && (
              <div className="flex items-center gap-2">
                {scaleConfig.type === "seconds" ? (
                  <span className="text-lg font-bold text-slate-800 tabular-nums">
                    {parseFloat(Number(score).toFixed(2))} s
                  </span>
                ) : scaleConfig.type === "mini_cog" ? (
                  <span className="text-sm font-semibold text-slate-800">
                    {score === true ? "Positivo" : score === false ? "Negativo" : "Pendiente"}
                  </span>
                ) : scaleConfig.type === "sppb" ? (
                  <>
                    <span className="text-lg font-bold text-slate-800">{score}</span>
                    <span className="text-xs text-slate-500">/12</span>
                  </>
                ) : scaleConfig.type === "cam" ? (
                  <span className="text-sm font-semibold text-slate-800">
                    {score === true ? "Positivo (CAM)" : score === false ? "Negativo (CAM)" : "Pendiente"}
                  </span>
                ) : scaleConfig.type === "polifarmacia_simple" ? (
                  <span className="text-lg font-bold text-slate-800 tabular-nums">
                    {answeredItems > 0 ? `${score} med.` : "—"}
                  </span>
                ) : (
                  <>
                    <span className="text-lg font-bold text-slate-800">{score}</span>
                    <span className="text-xs text-slate-500">/{scaleConfig.maxScore}</span>
                  </>
                )}
                {interpretation?.text ? (
                  <Badge className={`text-xs ${
                    interpretation.risk === "alto" ? "bg-red-100 text-red-700" :
                    interpretation.risk === "medio" ? "bg-amber-100 text-amber-700" :
                    "bg-emerald-100 text-emerald-700"
                  }`}>
                    {interpretation.text}
                  </Badge>
                ) : null}
              </div>
            )}
            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </div>
        </div>
        <Progress value={progress} className="h-1 mt-2" />
        <p className="text-xs text-slate-500 mt-1">
          {scaleConfig.type === "seconds"
            ? answeredItems > 0
              ? "Tiempo registrado"
              : "Pendiente"
            : scaleConfig.type === "score_0_30"
              ? answeredItems > 0
                ? "Puntaje registrado"
                : "Pendiente"
            : scaleConfig.type === "charlson_checklist" || scaleConfig.type === "score_0_10"
              ? answeredItems > 0
                ? "Puntaje registrado"
                : "Pendiente"
            : scaleConfig.type === "polifarmacia_simple"
              ? answeredItems > 0
                ? "Recuento registrado"
                : "Pendiente"
            : scaleConfig.type === "cam"
              ? `${answeredItems}/${totalItems} criterios`
            : scaleConfig.type === "mini_cog"
              ? `${answeredItems}/${totalItems} componentes`
            : scaleConfig.type === "sppb"
              ? `${answeredItems}/${totalItems} componentes`
            : `${answeredItems}/${totalItems} ítems`}
        </p>
      </CardHeader>
      
      {expanded && (
        <CardContent className="p-0 pt-0">
          <div className="relative">
            <div
              ref={scrollBodyRef}
              className="max-h-[400px] overflow-y-auto px-4 pb-4 pt-0"
              onScroll={updateScrollMoreHint}
            >
              {renderItems()}
            </div>
            {showScrollMoreHint && (
              <div
                className="pointer-events-none absolute bottom-0 left-0 right-0 flex justify-center pb-2 pt-6 bg-gradient-to-t from-white via-white/90 to-transparent"
                aria-hidden
              >
                <span className="inline-flex items-center gap-1 rounded-full border border-sky-200 bg-sky-50/95 px-3 py-1.5 text-xs font-medium text-sky-800 shadow-sm animate-bounce">
                  ↓ Desliza para completar
                </span>
              </div>
            )}
          </div>
        </CardContent>
      )}
    </Card>
  );
};

// ============ SCALES EVALUATION CONTAINER ============
export const ScalesEvaluationContainer = ({ selectedScales, scaleAnswers, onScaleAnswersChange }) => {
  const scaleNames = {
    barthel: "Índice de Barthel (0-100)",
    mmse: "Mini-Mental MMSE (0-30)",
    mna_sf: "MNA-SF Nutrición (0-14)",
    yesavage: "Yesavage GDS-15 (0-15)",
    phq9: "PHQ-9 (0-27)",
    gad7: "GAD-7 (0-21)",
    katz: "Índice de Katz ADL (0-6)",
    lawton: "Lawton IADL (0-8)",
    morse: "Escala de Morse (0-125)",
    tinetti: "Tinetti Marcha/Equilibrio (0-28)",
    frail: "Escala FRAIL (0-5)",
    sarc_f: "SARC-F (0-10)",
    cfs: "Clinical Frailty Scale (CFS) (1-9)",
    charlson: "Índice de Charlson (0-37)",
    eva_dolor: "EVA (Escala Visual Análoga) (0-10)",
    sppb: "SPPB (Short Physical Performance Battery)",
    moca: "MoCA (Montreal Cognitive Assessment) (0-30)",
    mini_cog: "Mini-Cog",
    cam: "CAM (Confusion Assessment Method)",
    pfeiffer: "Pfeiffer SPMSQ (0-10 errores)",
    norton: "Escala de Norton (5-20)",
    gijon: "Escala de Gijón Sociofamiliar (5-25)",
    zarit: "Zarit (Sobrecarga del cuidador) (0–48)",
    tug: "TUG (Timed Up and Go) — segundos",
    polifarmacia_simple: "Polifarmacia simple (recuento)",
  };

  if (selectedScales.length === 0) {
    return (
      <div className="text-center py-8 text-slate-500">
        <p>No se han seleccionado escalas para evaluar.</p>
        <p className="text-sm mt-1">Regrese al paso anterior para seleccionar escalas.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-sky-50 border border-sky-200 rounded-lg p-3">
        <p className="text-sm text-sky-800">
          Complete cada escala seleccionando las respuestas. El puntaje se calcula automáticamente.
        </p>
      </div>
      
      {selectedScales.map(scaleId => (
        <InteractiveScaleCard
          key={scaleId}
          scaleId={scaleId}
          scaleName={scaleNames[scaleId] || scaleId}
          answers={scaleAnswers[scaleId] || {}}
          onAnswersChange={(newAnswers) => {
            onScaleAnswersChange({
              ...scaleAnswers,
              [scaleId]: newAnswers
            });
          }}
          initiallyExpanded={selectedScales.length === 1}
        />
      ))}
    </div>
  );
};

export default InteractiveScaleCard;
